// Phase 6 — inline activity marker for coding-mode events surfaced by
// the bridge. Subtle by design: this is a status signal, not chat
// content. The PM agent never sees these entries.

import { useT, type MessageKey } from "../../i18n/index.js";

interface CodingProgressProps {
  readonly entry:
    | { readonly kind: "user_prompt"; readonly text: string }
    | {
        readonly kind: "file_touched";
        readonly path: string;
        readonly op: "read" | "edit" | "write";
      }
    | { readonly kind: "commit"; readonly sha: string; readonly message: string }
    | { readonly kind: "bash"; readonly command: string }
    | { readonly kind: "assistant_text"; readonly text: string };
}

const opIcon = (op: "read" | "edit" | "write"): string => {
  switch (op) {
    case "read":
      return "[r]";
    case "edit":
      return "[e]";
    case "write":
      return "[+]";
  }
};

const opLabelKey = (op: "read" | "edit" | "write"): MessageKey => {
  switch (op) {
    case "read":
      return "chatRender.fileRead";
    case "edit":
      return "chatRender.fileEdited";
    case "write":
      return "chatRender.fileCreated";
  }
};

export function CodingProgress({ entry }: CodingProgressProps) {
  const t = useT();
  switch (entry.kind) {
    case "file_touched":
      return (
        <div
          data-testid="coding-progress"
          data-kind="file_touched"
          data-op={entry.op}
          className="px-2 py-1 text-xs text-ink-subtle"
        >
          {opIcon(entry.op)} {t(opLabelKey(entry.op))}{" "}
          <code className="font-mono">{entry.path}</code>
        </div>
      );
    case "commit":
      return (
        <div
          data-testid="coding-progress"
          data-kind="commit"
          className="px-2 py-1 text-xs text-success-fg"
        >
          ✓ {t("chatRender.committed", { message: entry.message })}
        </div>
      );
    case "bash":
      return (
        <div
          data-testid="coding-progress"
          data-kind="bash"
          className="px-2 py-1 text-xs text-ink-subtle"
        >
          $ <code className="font-mono">{entry.command}</code>
        </div>
      );
    case "user_prompt":
      // user_prompt is already echoed in the timeline via the regular
      // user_message path on send — rendering it twice would duplicate.
      // Keep the entry available for tests but render nothing.
      return null;
    case "assistant_text":
      // Filtered upstream; included for exhaustiveness.
      return null;
  }
}
